import { uid } from "../kern/format.js";

/* ================================================================
   Weinkarte und Bestand.

   Eine Flasche auf der Weinkarte ist dem Keller entnommen, aber nicht
   getrunken. Deshalb:

   - Der Kellerbestand sinkt beim Aufnehmen und steigt beim Entfernen.
   - Protokolliert wird eine Bewegung der Art „weinkarte". Sie zählt
     wie jede Nicht-Zugangsbewegung als Abgang, taucht aber NICHT in
     den Entnahmen auf — sonst erschiene der Wein als getrunken und
     verfälschte Verbrauch und Statistik.
   - Eintrag und Bewegung teilen sich eine ID. Wird der Eintrag
     entfernt, verschwindet auch die Bewegung: Eine zurückgenommene
     Reservierung ist kein Ereignis, das im Verlauf stehen sollte,
     und der Bestand bleibt aus dem Protokoll ableitbar.

   Alle Funktionen sind rein: sie liefern ein neues Kellerbuch.
   ================================================================ */

const jgVon = (db, weinId, jgId) => {
  const w = (db.weine || []).find((x) => x.id === weinId);
  if (!w) return null;
  return (w.jahrgaenge || []).find((j) => j.id === jgId) || null;
};

/* Wie viele Flaschen dieser Größe liegen aktuell im Keller? */
function verfuegbar(db, weinId, jgId, groesse) {
  const jg = jgVon(db, weinId, jgId);
  if (!jg) return 0;
  return Number((jg.bestaende || {})[groesse]) || 0;
}

/* Flaschengrößen mit Bestand, für die Auswahl im Formular. */
function groessenMitBestand(jg) {
  return Object.entries((jg && jg.bestaende) || {})
    .filter(([, n]) => (Number(n) || 0) > 0)
    .map(([g]) => g);
}

const flaschenSumme = (jg) =>
  Object.values((jg && jg.bestaende) || {}).reduce((s, n) => s + (Number(n) || 0), 0);

/* Weine, die überhaupt etwas im Keller haben. */
function weineMitBestand(db) {
  return (db.weine || []).filter((w) => (w.jahrgaenge || []).some((j) => flaschenSumme(j) > 0));
}

/* Bestand einer Flaschengröße verändern; nie unter null. */
function bucheBestand(db, weinId, jgId, groesse, delta) {
  return {
    ...db,
    weine: (db.weine || []).map((w) => w.id !== weinId ? w : {
      ...w,
      jahrgaenge: (w.jahrgaenge || []).map((j) => {
        if (j.id !== jgId) return j;
        const bestaende = { ...(j.bestaende || {}) };
        bestaende[groesse] = Math.max(0, (Number(bestaende[groesse]) || 0) + delta);
        return { ...j, bestaende };
      }),
    }),
  };
}

const karteMap = (db, karteId, fn) => ({
  ...db,
  weinkarten: (db.weinkarten || []).map((k) => k.id === karteId ? fn(k) : k),
});

/* --- Aufnehmen ------------------------------------------------ */
function eintragHinzufuegen(db, karteId, { weinId, jgId, groesse, menge, preis = null, notiz = "" }) {
  const anzahl = Math.floor(Number(menge) || 0);
  if (anzahl < 1) return { db, fehler: "Bitte mindestens eine Flasche angeben." };
  const frei = verfuegbar(db, weinId, jgId, groesse);
  if (frei < 1) return { db, fehler: "Von diesem Jahrgang liegt nichts im Keller." };
  if (anzahl > frei) return { db, fehler: `Es liegen nur ${frei} Flaschen im Keller.` };

  const wein = (db.weine || []).find((w) => w.id === weinId);
  const jg = jgVon(db, weinId, jgId);
  if (!wein || !jg) return { db, fehler: "Wein oder Jahrgang nicht gefunden." };

  const id = uid();
  const datum = new Date().toISOString();
  let neu = bucheBestand(db, weinId, jgId, groesse, -anzahl);
  neu = karteMap(neu, karteId, (k) => ({
    ...k,
    eintraege: [...(k.eintraege || []), { id, weinId, jgId, groesse, menge: anzahl, preis, notiz }],
  }));
  neu = {
    ...neu,
    bewegungen: [...(neu.bewegungen || []),
      { id, art: "weinkarte", weinId, jgId, jahr: jg.jahr, groesse, menge: anzahl, preis: null, datum, karteId }],
  };
  return { db: neu, fehler: null };
}

/* --- Menge ändern --------------------------------------------- */
function eintragMenge(db, karteId, eintragId, neueMenge) {
  const karte = (db.weinkarten || []).find((k) => k.id === karteId);
  const eintrag = karte && (karte.eintraege || []).find((e) => e.id === eintragId);
  if (!eintrag) return { db, fehler: null };
  if (eintrag.menge == null) return { db, fehler: null };   /* Alteintrag ohne Buchung */

  const anzahl = Math.floor(Number(neueMenge) || 0);
  if (anzahl < 1) return { db, fehler: "Mindestens eine Flasche." };
  const delta = anzahl - eintrag.menge;
  if (delta === 0) return { db, fehler: null };
  const frei = verfuegbar(db, eintrag.weinId, eintrag.jgId, eintrag.groesse);
  if (delta > frei) return { db, fehler: `Es liegen nur noch ${frei} Flaschen im Keller.` };

  let neu = bucheBestand(db, eintrag.weinId, eintrag.jgId, eintrag.groesse, -delta);
  neu = karteMap(neu, karteId, (k) => ({
    ...k,
    eintraege: k.eintraege.map((e) => e.id === eintragId ? { ...e, menge: anzahl } : e),
  }));
  neu = {
    ...neu,
    bewegungen: (neu.bewegungen || []).map((b) => b.id === eintragId ? { ...b, menge: anzahl } : b),
  };
  return { db: neu, fehler: null };
}

/* --- Entnehmen: zurück in den Keller -------------------------- */
function eintragEntfernen(db, karteId, eintragId) {
  const karte = (db.weinkarten || []).find((k) => k.id === karteId);
  const eintrag = karte && (karte.eintraege || []).find((e) => e.id === eintragId);
  if (!eintrag) return { db, fehler: null };

  let neu = db;
  if (eintrag.menge != null && eintrag.groesse) {
    neu = bucheBestand(neu, eintrag.weinId, eintrag.jgId, eintrag.groesse, eintrag.menge);
    neu = { ...neu, bewegungen: (neu.bewegungen || []).filter((b) => b.id !== eintragId) };
  }
  neu = karteMap(neu, karteId, (k) => ({
    ...k, eintraege: k.eintraege.filter((e) => e.id !== eintragId),
  }));
  return { db: neu, fehler: null };
}

/* --- Ausschenken: von der Karte konsumiert --------------------
   Die Flasche war über die Reservierung bereits aus dem Keller
   gebucht. Beim Ausschenken wird die Reservierung um diese Menge
   verkleinert und stattdessen eine echte Entnahme gebucht — der
   Bestand ändert sich dadurch NICHT doppelt. Die Entnahme trägt die
   Weinkarte mit, damit die Statistik sie zuordnen kann. */
function eintragKonsumieren(db, karteId, eintragId, menge = 1) {
  const karte = (db.weinkarten || []).find((k) => k.id === karteId);
  const eintrag = karte && (karte.eintraege || []).find((e) => e.id === eintragId);
  if (!eintrag) return { db, fehler: null };
  if (eintrag.menge == null) return { db, fehler: "Dieser Eintrag führt keine Flaschen." };

  const anzahl = Math.floor(Number(menge) || 0);
  if (anzahl < 1) return { db, fehler: "Mindestens eine Flasche." };
  if (anzahl > eintrag.menge) return { db, fehler: `Auf der Karte stehen nur ${eintrag.menge} Flaschen.` };

  const jg = jgVon(db, eintrag.weinId, eintrag.jgId);
  const rest = eintrag.menge - anzahl;
  const datum = new Date().toISOString();
  const eid = uid();

  let neu = karteMap(db, karteId, (k) => ({
    ...k,
    eintraege: rest > 0
      ? k.eintraege.map((e) => e.id === eintragId ? { ...e, menge: rest } : e)
      : k.eintraege.filter((e) => e.id !== eintragId),
  }));

  /* Reservierung verkleinern bzw. auflösen. */
  neu = {
    ...neu,
    bewegungen: (neu.bewegungen || [])
      .map((b) => b.id === eintragId ? { ...b, menge: rest } : b)
      .filter((b) => !(b.id === eintragId && rest <= 0)),
  };

  const einstand = jg && jg.preis != null ? jg.preis : null;
  neu = {
    ...neu,
    entnahmen: [...(neu.entnahmen || []), {
      id: eid, weinId: eintrag.weinId, jgId: eintrag.jgId, jahr: jg ? jg.jahr : null,
      groesse: eintrag.groesse, menge: anzahl, preis: einstand, datum,
      karteId, karteTitel: karte.titel || "", kartePreis: eintrag.preis != null ? eintrag.preis : null,
    }],
    bewegungen: [...(neu.bewegungen || []), {
      id: eid, art: "entnahme", weinId: eintrag.weinId, jgId: eintrag.jgId,
      jahr: jg ? jg.jahr : null, groesse: eintrag.groesse, menge: anzahl,
      preis: einstand, datum, karteId,
    }],
  };
  return { db: neu, fehler: null };
}

/* --- Ganze Karte auflösen ------------------------------------- */
function karteEntfernen(db, karteId) {
  const karte = (db.weinkarten || []).find((k) => k.id === karteId);
  if (!karte) return db;
  let neu = db;
  for (const e of karte.eintraege || []) {
    if (e.menge == null || !e.groesse) continue;
    neu = bucheBestand(neu, e.weinId, e.jgId, e.groesse, e.menge);
    neu = { ...neu, bewegungen: (neu.bewegungen || []).filter((b) => b.id !== e.id) };
  }
  return { ...neu, weinkarten: (neu.weinkarten || []).filter((k) => k.id !== karteId) };
}

export {
  eintragEntfernen, eintragHinzufuegen, eintragKonsumieren, eintragMenge, flaschenSumme,
  groessenMitBestand, karteEntfernen, verfuegbar, weineMitBestand,
};
